<template>
  <div class="space-y-6">
    <div class="bg-gradient-to-r from-slate-600 to-slate-800 rounded-lg p-6 text-white">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold">Documentos Legales</h1>
          <p class="text-slate-200 mt-1">Gestiona las políticas de privacidad y términos y condiciones</p>
        </div>
        <div class="hidden sm:block">
          <div class="bg-white/20 rounded-lg p-4">
            <svg class="h-12 w-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabs -->
    <div class="card p-4 border-b border-gray-200">
      <nav class="-mb-px flex space-x-8">
        <button
          @click="activeTab = 'privacy'"
          :class="[
            'py-2 px-1 border-b-2 font-medium text-sm',
            activeTab === 'privacy'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          ]"
        >
          Políticas de Privacidad
        </button>
        <button
          @click="activeTab = 'terms'"
          :class="[
            'py-2 px-1 border-b-2 font-medium text-sm',
            activeTab === 'terms'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          ]"
        >
          Términos y Condiciones
        </button>
      </nav>
    </div>

    <!-- Privacy Policy Tab -->
    <div v-if="activeTab === 'privacy'" class="space-y-6">
      <div class="flex justify-between items-center">
        <h2 class="text-lg font-semibold text-gray-900">Políticas de Privacidad</h2>
        <div class="flex space-x-3">
          <button
            v-if="activePrivacyPolicy"
            @click="openPreviewWindow('privacy')"
            class="px-4 py-2 rounded-md transition-opacity hover:opacity-90"
            style="background-color: #475569; color: #fff"
          >
            Vista Previa
          </button>
          <button
            @click="router.push({ path: '/legal/create', query: { type: 'privacy' } })"
            class="px-4 py-2 rounded-md transition-opacity hover:opacity-90"
            style="background-color: #1d4ed8; color: #fff"
          >
            Nueva Política
          </button>
        </div>
      </div>

      <!-- Active Privacy Policy -->
      <div v-if="activePrivacyPolicy" class="bg-green-50 border border-green-200 rounded-lg p-4">
        <div class="flex items-center justify-between">
          <div>
            <h3 class="font-medium text-green-900">{{ activePrivacyPolicy.title }}</h3>
            <p class="text-sm text-green-700">Versión {{ activePrivacyPolicy.version }} - Activa</p>
            <p class="text-xs text-green-600 mt-1">
              Creada: {{ formatDate(activePrivacyPolicy.createdAt) }}
            </p>
          </div>
          <div class="flex space-x-2">
            <button
              @click="openEditModal('privacy', activePrivacyPolicy)"
              class="text-blue-600 hover:text-blue-800 text-sm"
            >
              Editar
            </button>
          </div>
        </div>
      </div>

      <div v-else class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p class="text-yellow-800">No hay una política de privacidad activa</p>
      </div>

      <!-- All Privacy Policies -->
      <div class="card">
        <div class="px-4 py-5 sm:p-6">
          <h3 class="text-lg font-medium text-gray-900 mb-4">Historial de Políticas</h3>
          <div v-if="privacyPolicies.length === 0" class="text-gray-500 text-center py-4">
            No hay políticas de privacidad registradas
          </div>
          <div v-else class="space-y-3">
            <div
              v-for="policy in privacyPolicies"
              :key="policy.id"
              class="border border-gray-200 rounded-lg p-4"
            >
              <div class="flex items-center justify-between">
                <div>
                  <h4 class="font-medium text-gray-900">{{ policy.title }}</h4>
                  <p class="text-sm text-gray-600">
                    Versión {{ policy.version }}
                    <span v-if="policy.isActive" class="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Activa
                    </span>
                  </p>
                  <p class="text-xs text-gray-500 mt-1">
                    Creada: {{ formatDate(policy.createdAt) }}
                  </p>
                </div>
                <div class="flex space-x-2">
                  <button
                    @click="openPreviewWindow('privacy')"
                    class="text-green-600 hover:text-green-800 text-sm"
                  >
                    Vista Previa
                  </button>
                  <button
                    v-if="!policy.isActive"
                    @click="activateDocument('privacy', policy.id)"
                    class="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Activar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Terms and Conditions Tab -->
    <div v-if="activeTab === 'terms'" class="space-y-6">
      <div class="flex justify-between items-center">
        <h2 class="text-lg font-semibold text-gray-900">Términos y Condiciones</h2>
        <div class="flex space-x-3">
          <button
            v-if="activeTermsAndConditions"
            @click="openPreviewWindow('terms')"
            class="px-4 py-2 rounded-md transition-opacity hover:opacity-90"
            style="background-color: #475569; color: #fff"
          >
            Vista Previa
          </button>
          <button
            @click="router.push({ path: '/legal/create', query: { type: 'terms' } })"
            class="px-4 py-2 rounded-md transition-opacity hover:opacity-90"
            style="background-color: #1d4ed8; color: #fff"
          >
            Nuevos Términos
          </button>
        </div>
      </div>

      <!-- Active Terms and Conditions -->
      <div v-if="activeTermsAndConditions" class="bg-green-50 border border-green-200 rounded-lg p-4">
        <div class="flex items-center justify-between">
          <div>
            <h3 class="font-medium text-green-900">{{ activeTermsAndConditions.title }}</h3>
            <p class="text-sm text-green-700">Versión {{ activeTermsAndConditions.version }} - Activa</p>
            <p class="text-xs text-green-600 mt-1">
              Creada: {{ formatDate(activeTermsAndConditions.createdAt) }}
            </p>
          </div>
          <div class="flex space-x-2">
            <button
              @click="openEditModal('terms', activeTermsAndConditions)"
              class="text-blue-600 hover:text-blue-800 text-sm"
            >
              Editar
            </button>
          </div>
        </div>
      </div>

      <div v-else class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p class="text-yellow-800">No hay términos y condiciones activos</p>
      </div>

      <!-- All Terms and Conditions -->
      <div class="card">
        <div class="px-4 py-5 sm:p-6">
          <h3 class="text-lg font-medium text-gray-900 mb-4">Historial de Términos</h3>
          <div v-if="termsAndConditions.length === 0" class="text-gray-500 text-center py-4">
            No hay términos y condiciones registrados
          </div>
          <div v-else class="space-y-3">
            <div
              v-for="terms in termsAndConditions"
              :key="terms.id"
              class="border border-gray-200 rounded-lg p-4"
            >
              <div class="flex items-center justify-between">
                <div>
                  <h4 class="font-medium text-gray-900">{{ terms.title }}</h4>
                  <p class="text-sm text-gray-600">
                    Versión {{ terms.version }}
                    <span v-if="terms.isActive" class="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Activa
                    </span>
                  </p>
                  <p class="text-xs text-gray-500 mt-1">
                    Creada: {{ formatDate(terms.createdAt) }}
                  </p>
                </div>
                <div class="flex space-x-2">
                  <button
                    @click="openPreviewWindow('terms')"
                    class="text-green-600 hover:text-green-800 text-sm"
                  >
                    Vista Previa
                  </button>
                  <button
                    v-if="!terms.isActive"
                    @click="activateDocument('terms', terms.id)"
                    class="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Activar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div class="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div class="mt-3">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-medium text-gray-900">
              {{ modalMode === 'create' ? 'Crear' : 'Editar' }}
              {{ modalType === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones' }}
            </h3>
            <button @click="closeModal" class="text-gray-400 hover:text-gray-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          <form @submit.prevent="submitForm" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Título</label>
              <input
                v-model="form.title"
                type="text"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ingrese el título"
              >
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Versión</label>
              <input
                v-model="form.version"
                type="text"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="ej: 1.0, 2.1, etc."
              >
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Contenido</label>
              <HtmlEditor
                v-model="form.content"
                placeholder="Ingrese el contenido del documento legal. Puede usar formato HTML para negritas, cursivas, listas, etc."
                height="500px"
              />
            </div>

            <div class="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                @click="closeModal"
                class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                :disabled="loading"
                class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {{ loading ? 'Guardando...' : 'Guardar' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Preview Modal -->
    <div v-if="showPreviewModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div class="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div class="mt-3">
          <div class="flex items-center justify-between mb-4">
            <div>
              <h3 class="text-lg font-medium text-gray-900">{{ previewDocumentData?.title }}</h3>
              <p class="text-sm text-gray-600">Versión {{ previewDocumentData?.version }}</p>
            </div>
            <button @click="closePreviewModal" class="text-gray-400 hover:text-gray-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          <div class="max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div class="prose max-w-none text-sm" v-html="sanitizeHtml(previewDocumentData?.content || '')"></div>
          </div>

          <div class="flex justify-end pt-4">
            <button
              @click="closePreviewModal"
              class="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import legalService, { type PrivacyPolicy, type TermsAndConditions } from '../services/legalService';
import HtmlEditor from '../components/HtmlEditor.vue';

const activeTab = ref<'privacy' | 'terms'>('privacy');
const loading = ref(false);
const router = useRouter();

// Data
const activePrivacyPolicy = ref<PrivacyPolicy | null>(null);
const activeTermsAndConditions = ref<TermsAndConditions | null>(null);
const privacyPolicies = ref<PrivacyPolicy[]>([]);
const termsAndConditions = ref<TermsAndConditions[]>([]);

// Modal state
const showModal = ref(false);
const modalMode = ref<'create' | 'edit'>('create');
const modalType = ref<'privacy' | 'terms'>('privacy');
const editingId = ref<number | null>(null);

// Preview modal
const showPreviewModal = ref(false);
const previewDocumentData = ref<PrivacyPolicy | TermsAndConditions | null>(null);

// Form
const form = ref({
  title: '',
  content: '',
  version: ''
});

const sanitizeHtml = (value: string) => {
  return value
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '')
    .replace(/\son\w+\s*=\s*(['"]).*?\1/gi, '')
    .replace(/\son\w+\s*=\s*[^\s>]+/gi, '')
    .replace(/javascript\s*:/gi, '')
    .replace(/data\s*:\s*text\/html/gi, '')
}

// Methods
const loadData = async () => {
  try {
    loading.value = true;
    
    // Load active documents
    const [activePrivacy, activeTerms] = await Promise.all([
      legalService.getActivePrivacyPolicy(),
      legalService.getActiveTermsAndConditions()
    ]);
    
    activePrivacyPolicy.value = activePrivacy;
    activeTermsAndConditions.value = activeTerms;
    
    // Load all documents
    const [allPrivacy, allTerms] = await Promise.all([
      legalService.getAllPrivacyPolicies(),
      legalService.getAllTermsAndConditions()
    ]);
    
    privacyPolicies.value = allPrivacy;
    termsAndConditions.value = allTerms;
  } catch (error) {
    console.error('Error loading legal documents:', error);
  } finally {
    loading.value = false;
  }
};

const openEditModal = (type: 'privacy' | 'terms', document: PrivacyPolicy | TermsAndConditions) => {
  modalMode.value = 'edit';
  modalType.value = type;
  editingId.value = document.id;
  form.value = {
    title: document.title,
    content: document.content,
    version: document.version
  };
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  form.value = { title: '', content: '', version: '' };
  editingId.value = null;
};

const submitForm = async () => {
  try {
    loading.value = true;
    
    if (modalMode.value === 'create') {
      if (modalType.value === 'privacy') {
        await legalService.createPrivacyPolicy(form.value);
      } else {
        await legalService.createTermsAndConditions(form.value);
      }
    } else if (editingId.value) {
      if (modalType.value === 'privacy') {
        await legalService.updatePrivacyPolicy(editingId.value, form.value);
      } else {
        await legalService.updateTermsAndConditions(editingId.value, form.value);
      }
    }
    
    closeModal();
    await loadData();
  } catch (error) {
    console.error('Error saving document:', error);
  } finally {
    loading.value = false;
  }
};

const activateDocument = async (type: 'privacy' | 'terms', id: number) => {
  try {
    loading.value = true;
    
    if (type === 'privacy') {
      await legalService.updatePrivacyPolicy(id, { isActive: true });
    } else {
      await legalService.updateTermsAndConditions(id, { isActive: true });
    }
    
    await loadData();
  } catch (error) {
    console.error('Error activating document:', error);
  } finally {
    loading.value = false;
  }
};

const openPreviewWindow = (type: 'privacy' | 'terms') => {
  const routeType = type === 'privacy' ? 'privacy-policy' : 'terms-and-conditions';
  const url = `/legal-preview/${routeType}`;
  window.open(url, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
};

const closePreviewModal = () => {
  showPreviewModal.value = false;
  previewDocumentData.value = null;
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

onMounted(() => {
  loadData();
});
</script>